import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  List,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import type { AgendaItem } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { AgendaRow, SOURCE_LABELS } from "../components/PlayerAgenda";
import { PlayerScheduleCalendar } from "../components/PlayerScheduleCalendar";
import { usePlayerAgenda } from "../hooks/usePlayerAgenda";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
type CalendarView = "month" | "agenda" | "week" | "three_day" | "day";
const VIEW_OPTIONS: ReadonlyArray<{ value: CalendarView; label: string }> = [
  { value: "month", label: "Month" },
  { value: "agenda", label: "Agenda" },
  { value: "week", label: "Week" },
  { value: "three_day", label: "3 days" },
  { value: "day", label: "Day" },
];
function startOfMonth(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), 1);
}

function addMonths(value: Date, amount: number): Date {
  return new Date(value.getFullYear(), value.getMonth() + amount, 1);
}

function addDays(value: Date, amount: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeek(value: Date): Date {
  return addDays(value, -value.getDay());
}

function parseDateKey(value: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
  );
  return dateKey(date) === value ? date : null;
}

function parseMonthKey(value: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const month = new Date(Number(match[1]), Number(match[2]) - 1, 1, 12);
  return monthKey(month) === value ? month : null;
}

function dateKey(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function monthKey(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}

function utcDateKey(value: Date): string {
  return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-${String(value.getUTCDate()).padStart(2, "0")}`;
}

function zonedDateKey(value: string, timezone: string): string | null {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(instant);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    return year && month && day
      ? `${year}-${month}-${day}`
      : utcDateKey(instant);
  } catch {
    return utcDateKey(instant);
  }
}

function itemDateKey(item: AgendaItem, displayTimezone: string): string | null {
  if (!item.starts_at) return null;
  const value = new Date(item.starts_at);
  if (Number.isNaN(value.getTime())) return null;
  return zonedDateKey(item.starts_at, displayTimezone);
}

function monthCells(month: Date): Date[] {
  const first = startOfMonth(month);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
}

function monthWindow(month: Date): { start: string; end: string } {
  const start = startOfMonth(month);
  const end = addMonths(month, 1);
  // Source and viewer timezones can differ by 25 hours. A two-day margin keeps
  // month-boundary occurrences available for source-timezone grouping.
  start.setDate(start.getDate() - 2);
  end.setDate(end.getDate() + 2);
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function rangeWindow(days: Date[]): { start: string; end: string } {
  const start = new Date(days[0]);
  const end = addDays(days[days.length - 1], 1);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 2);
  end.setDate(end.getDate() + 2);
  return { start: start.toISOString(), end: end.toISOString() };
}

const monthLabel = (month: Date) =>
  new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(
    month,
  );

const dayLabel = (day: Date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(day);

const compactDayLabel = (day: Date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(day);

function scheduleDays(
  view: Exclude<CalendarView, "month" | "agenda">,
  selected: Date,
): Date[] {
  const first = view === "week" ? startOfWeek(selected) : selected;
  const count = view === "week" ? 7 : view === "three_day" ? 3 : 1;
  return Array.from({ length: count }, (_, index) => addDays(first, index));
}

function resolveDisplayTimezone(requested: string | null): string {
  const fallback = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  if (!requested) return fallback;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: requested }).format(
      new Date(),
    );
    return requested;
  } catch {
    return fallback;
  }
}

export function PersonalCalendarPage() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Page title="Calendar">
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          <Typography color="text.secondary">
            Sign in to see your calendar.
          </Typography>
          <Button
            component={RouterLink}
            to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
          >
            Sign in
          </Button>
        </Stack>
      </Page>
    );
  }
  return <PersonalCalendarBody key={user.uid} />;
}

function PersonalCalendarBody() {
  const today = useMemo(() => new Date(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const displayTimezone = resolveDisplayTimezone(searchParams.get("timezone"));
  const requestedView = searchParams.get("view");
  const view: CalendarView = VIEW_OPTIONS.some(
    (option) => option.value === requestedView,
  )
    ? (requestedView as CalendarView)
    : "month";
  const selectedDay = parseDateKey(searchParams.get("date")) ?? today;
  const [pendingFocusKey, setPendingFocusKey] = useState<string | null>(null);
  const dayRefs = useRef(new Map<string, HTMLButtonElement>());
  const month =
    parseMonthKey(searchParams.get("month")) ?? startOfMonth(selectedDay);
  const visibleDays = useMemo(
    () =>
      view === "month" || view === "agenda"
        ? []
        : scheduleDays(view, selectedDay),
    [selectedDay, view],
  );
  const queryWindow = useMemo(() => {
    if (view === "agenda") {
      return rangeWindow([selectedDay, addDays(selectedDay, 87)]);
    }
    const base = monthWindow(month);
    if (view === "month") return base;
    const visible = rangeWindow(visibleDays);
    return {
      start: base.start < visible.start ? base.start : visible.start,
      end: base.end > visible.end ? base.end : visible.end,
    };
  }, [month, view, visibleDays]);
  const agenda = usePlayerAgenda("upcoming", 50, queryWindow);

  useEffect(() => {
    if (
      agenda.current &&
      agenda.hasNextPage &&
      !agenda.isFetchingNextPage &&
      !agenda.partial
    ) {
      void agenda.fetchNextPage();
    }
  }, [
    agenda.current,
    agenda.hasNextPage,
    agenda.isFetchingNextPage,
    agenda.partial,
    agenda.fetchNextPage,
  ]);

  const cells = useMemo(() => monthCells(month), [month]);
  const itemsByDay = useMemo(() => {
    const grouped = new Map<string, AgendaItem[]>();
    for (const item of agenda.items) {
      const key = itemDateKey(item, displayTimezone);
      if (!key) continue;
      grouped.set(key, [...(grouped.get(key) ?? []), item]);
    }
    return grouped;
  }, [agenda.items, displayTimezone]);
  const selectedItems = itemsByDay.get(dateKey(selectedDay)) ?? [];
  const unavailable = agenda.unavailable.map((source) => SOURCE_LABELS[source]);

  useEffect(() => {
    if (!pendingFocusKey) return;
    dayRefs.current.get(pendingFocusKey)?.focus();
    setPendingFocusKey(null);
  }, [cells, pendingFocusKey]);

  function persist(
    nextView: CalendarView,
    nextDate: Date,
    nextMonth = startOfMonth(nextDate),
  ) {
    const params = new URLSearchParams(searchParams);
    params.set("view", nextView);
    params.set("date", dateKey(nextDate));
    if (nextView === "month") params.set("month", monthKey(nextMonth));
    else params.delete("month");
    setSearchParams(params, { replace: true });
  }

  function changeView(nextView: CalendarView) {
    persist(nextView, selectedDay, month);
  }

  function movePeriod(amount: number) {
    if (view === "month") {
      const nextMonth = addMonths(month, amount);
      persist(view, nextMonth, nextMonth);
      return;
    }
    const next = addDays(
      selectedDay,
      amount *
        (view === "agenda"
          ? 30
          : view === "week"
            ? 7
            : view === "three_day"
              ? 3
              : 1),
    );
    persist(view, next);
  }

  function selectDay(day: Date) {
    persist(view, day, month);
  }

  function moveGridFocus(day: Date, event: KeyboardEvent<HTMLButtonElement>) {
    const next = new Date(day);
    switch (event.key) {
      case "ArrowLeft":
        next.setDate(next.getDate() - 1);
        break;
      case "ArrowRight":
        next.setDate(next.getDate() + 1);
        break;
      case "ArrowUp":
        next.setDate(next.getDate() - 7);
        break;
      case "ArrowDown":
        next.setDate(next.getDate() + 7);
        break;
      case "Home":
        next.setDate(next.getDate() - next.getDay());
        break;
      case "End":
        next.setDate(next.getDate() + (6 - next.getDay()));
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextKey = dateKey(next);
    persist(
      view,
      next,
      cells.some((cell) => dateKey(cell) === nextKey)
        ? month
        : startOfMonth(next),
    );
    setPendingFocusKey(dateKey(next));
  }

  const periodTitle =
    view === "month"
      ? monthLabel(month)
      : view === "agenda"
        ? `Upcoming from ${compactDayLabel(selectedDay)}`
      : visibleDays.length === 1
        ? dayLabel(visibleDays[0])
        : `${compactDayLabel(visibleDays[0])} – ${compactDayLabel(visibleDays[visibleDays.length - 1])}`;
  const previousTarget =
    view === "month"
      ? addMonths(month, -1)
      : addDays(
          selectedDay,
          -(
            view === "agenda"
              ? 30
              : view === "week"
                ? 7
                : view === "three_day"
                  ? 3
                  : 1
          ),
        );
  const previousDisabled =
    (view === "month"
      ? startOfMonth(previousTarget)
      : previousTarget
    ).getTime() <
    (view === "month"
      ? startOfMonth(today)
      : new Date(today.getFullYear(), today.getMonth(), today.getDate())
    ).getTime();
  const loading =
    agenda.isLoading || (agenda.hasNextPage && agenda.isFetchingNextPage);

  return (
    <Page title="Calendar">
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ alignItems: { md: "center" }, justifyContent: "space-between" }}
        >
          <Typography color="text.secondary" variant="caption" sx={{ minWidth: 0, flex: 1 }}>
            Times shown in {displayTimezone.replaceAll("_", " ")}.
          </Typography>
          <Stack
            aria-label="Calendar view"
            direction="row"
            role="group"
            spacing={0.5}
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              flexShrink: 0,
              p: 0.5,
              borderRadius: 2.5,
              bgcolor: "action.hover",
              scrollbarWidth: "none",
            }}
          >
            {VIEW_OPTIONS.map((option) => (
              <Button
                aria-pressed={view === option.value}
                key={option.value}
                onClick={() => changeView(option.value)}
                size="small"
                color="inherit"
                variant="text"
                sx={{
                  flexShrink: 0,
                  borderRadius: 2,
                  px: 1.5,
                  fontWeight: view === option.value ? 700 : 500,
                  color: view === option.value ? "text.primary" : "text.secondary",
                  bgcolor: view === option.value ? "background.default" : "transparent",
                }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Button
            aria-label={view === "month" ? "Previous month" : "Previous period"}
            disabled={previousDisabled}
            onClick={() => movePeriod(-1)}
            size="small"
            variant="outlined"
            sx={{ minWidth: 44 }}
          >
            <ChevronLeftRounded />
          </Button>
          <Button
            aria-label={view === "month" ? "Next month" : "Next period"}
            onClick={() => movePeriod(1)}
            size="small"
            variant="outlined"
            sx={{ minWidth: 44 }}
          >
            <ChevronRightRounded />
          </Button>
          <Button
            disabled={dateKey(selectedDay) === dateKey(today)}
            onClick={() => {
              persist(view, today, startOfMonth(today));
            }}
            size="small"
            variant="outlined"
          >
            Today
          </Button>
          <Typography
            aria-live="polite"
            component="h2"
            variant="h6"
            sx={{ ml: { sm: 1 }, fontWeight: 700 }}
          >
            {periodTitle}
          </Typography>
        </Stack>
        {agenda.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => void agenda.restart()}>
                Retry
              </Button>
            }
          >
            We couldn&apos;t load your calendar.
          </Alert>
        ) : agenda.partial ? (
          <Alert severity="warning">
            Some activity is unavailable
            {unavailable.length ? `: ${unavailable.join(", ")}.` : "."}
          </Alert>
        ) : null}

        {view === "month" ? (
          <>
            <Paper
              variant="outlined"
              sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3 }}
            >
              <Stack spacing={2}>
                <Box
                  role="grid"
                  aria-label={monthLabel(month)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.5,
                  }}
                >
                  <Box role="row" sx={{ display: "contents" }}>
                    {WEEKDAYS.map((weekday) => (
                      <Typography
                        key={weekday}
                        role="columnheader"
                        align="center"
                        color="text.secondary"
                        variant="caption"
                        sx={{ py: 0.5, fontWeight: 700 }}
                      >
                        {weekday}
                      </Typography>
                    ))}
                  </Box>
                  {Array.from({ length: 6 }, (_, week) => (
                    <Box key={week} role="row" sx={{ display: "contents" }}>
                      {cells.slice(week * 7, week * 7 + 7).map((day) => {
                        const key = dateKey(day);
                        const count = itemsByDay.get(key)?.length ?? 0;
                        const inMonth = day.getMonth() === month.getMonth();
                        const selected = key === dateKey(selectedDay);
                        return (
                          <ButtonBase
                            key={key}
                            ref={(node) => {
                              if (node) dayRefs.current.set(key, node);
                              else dayRefs.current.delete(key);
                            }}
                            role="gridcell"
                            aria-label={`${dayLabel(day)}${count ? `, ${count} ${count === 1 ? "event" : "events"}` : ""}`}
                            aria-selected={selected}
                            onClick={() => selectDay(day)}
                            onKeyDown={(event) => moveGridFocus(day, event)}
                            tabIndex={selected ? 0 : -1}
                            sx={{
                              minWidth: 0,
                              minHeight: { xs: 46, sm: 58 },
                              borderRadius: 2,
                              border: 1,
                              borderColor: selected
                                ? "primary.main"
                                : "transparent",
                              bgcolor: selected
                                ? "primary.main"
                                : count
                                  ? "action.selected"
                                  : "transparent",
                              color: selected
                                ? "primary.contrastText"
                                : inMonth
                                  ? "text.primary"
                                  : "text.disabled",
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.25,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: selected || count ? 700 : 500 }}
                            >
                              {day.getDate()}
                            </Typography>
                            {count ? (
                              <Box
                                aria-hidden
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  bgcolor: selected
                                    ? "primary.contrastText"
                                    : "primary.main",
                                }}
                              />
                            ) : null}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Paper>

            <Stack
              component="section"
              aria-labelledby="selected-day-heading"
              spacing={1.5}
            >
              <Typography
                id="selected-day-heading"
                component="h2"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                {dayLabel(selectedDay)}
              </Typography>
              {loading ? (
                <Stack
                  direction="row"
                  spacing={1}
                  role="status"
                  sx={{ alignItems: "center" }}
                >
                  <CircularProgress size={18} />
                  <Typography color="text.secondary">
                    Loading your calendar…
                  </Typography>
                </Stack>
              ) : !agenda.isError ? (
                <>
                  {selectedItems.length ? (
                    <List disablePadding>
                      {selectedItems.map((item) => (
                        <AgendaRow
                          key={`${item.key.source}:${item.key.row_id}`}
                          item={item}
                          displayTimezone={displayTimezone}
                        />
                      ))}
                    </List>
                  ) : !agenda.partial ? (
                    <Typography color="text.secondary">
                      Nothing scheduled for this day.
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </>
        ) : view === "agenda" ? (
          <AgendaTimeline
            items={agenda.items}
            loading={loading}
            displayTimezone={displayTimezone}
          />
        ) : (
          <PlayerScheduleCalendar
            date={selectedDay}
            displayTimezone={displayTimezone}
            items={agenda.items}
            loading={loading}
            view={view}
          />
        )}
      </Stack>
    </Page>
  );
}

function AgendaTimeline({
  items,
  loading,
  displayTimezone,
}: {
  items: AgendaItem[];
  loading: boolean;
  displayTimezone: string;
}) {
  if (loading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        role="status"
        sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress size={18} />
        <Typography color="text.secondary">Loading your agenda…</Typography>
      </Stack>
    );
  }
  if (items.length === 0) {
    return (
      <Typography color="text.secondary">
        No upcoming activity in this window.
      </Typography>
    );
  }
  // Group consecutive items by their day in the display timezone.
  const groups: { key: string; label: string; items: AgendaItem[] }[] = [];
  for (const item of items) {
    const key = itemDateKey(item, displayTimezone) ?? "undated";
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, label: dayGroupLabel(key), items: [item] });
  }
  return (
    <Stack spacing={1.5} role="list" aria-label="Chronological agenda">
      {groups.map((group, index) => (
        <Box key={`${group.key}-${index}`} role="listitem">
          <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
            {group.label}
          </Typography>
          <List disablePadding>
            {group.items.map((item) => (
              <AgendaRow
                key={`${item.key.source}:${item.key.row_id}`}
                item={item}
                displayTimezone={displayTimezone}
                titleComponent="h4"
              />
            ))}
          </List>
        </Box>
      ))}
    </Stack>
  );
}

/** "Sat, Oct 31" for a YYYY-MM-DD day key (that calendar day, not an instant). */
function dayGroupLabel(key: string): string {
  const day = parseDateKey(key);
  if (!day) return "Due dates";
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(day);
}
